import * as R from 'ramda';
import moment from 'moment';
import { combineReducers } from 'redux';
import * as usersReducer from '../../core/reducers/users';
import {
  flattenComments,
  mergeComment,
  storePostedComment,
  updateStoredComment,
} from '../utils/comment-thread';
import {
  SOCIAL_POST_COMMENT,
  SOCIAL_POST_CONGRATULATION,
  SOCIAL_DELETE_MESSAGE,
  SOCIAL_RECEIVE_COMMENTS,
  SOCIAL_RECEIVE_CONGRATULATIONS,
  SOCIAL_DELETE_COMMENT,
  SOCIAL_UPDATE_COMMENT,
  SOCIAL_COMMENT_HIGH_FIVE,
  SOCIAL_TRANSLATE_COMMENT,
} from '../actions';

const items = (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_COMMENTS:
    case SOCIAL_RECEIVE_CONGRATULATIONS:
      return { ...state, ...flattenComments(action.comments || []) };
    case SOCIAL_POST_COMMENT:
    case SOCIAL_POST_CONGRATULATION:
      return storePostedComment(state, action.comment);
    case SOCIAL_COMMENT_HIGH_FIVE:
    case SOCIAL_UPDATE_COMMENT: {
      if (!action.comment?.id) return state;
      if (!state[action.comment.id]) return { ...state, [action.comment.id]: action.comment };
      return updateStoredComment(state, action.comment.id, (existing) => (
        mergeComment(existing, action.comment)
      ));
    }
    case SOCIAL_DELETE_COMMENT:
      return updateStoredComment(state, action.commentId, (comment) => ({
        ...comment,
        text: null,
        deleted_at: moment().toISOString(),
      }));
    case SOCIAL_TRANSLATE_COMMENT:
      return updateStoredComment(state, action.commentId, (comment) => ({
        ...comment,
        translated_text: {
          ...(comment.translated_text || {}),
          [action.locale]: action.translated_text,
        },
      }));
    default: return state;
  }
};

const addCommentId = R.curry((id, comments) => R.append(id, comments || []));

const messages = (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_COMMENTS:
    case SOCIAL_RECEIVE_CONGRATULATIONS:
      return R.assoc(action.messageId, R.pluck('id', action.comments), state);
    case SOCIAL_DELETE_MESSAGE:
      return R.omit([action.messageId], state);
    case SOCIAL_POST_COMMENT:
    case SOCIAL_POST_CONGRATULATION:
      if (action.comment?.parent_id) return state;
      return R.assoc(
        action.messageId,
        addCommentId(action.comment.id, state[action.messageId]),
        state,
      );
    case SOCIAL_UPDATE_COMMENT: {
      const messageId = action.comment?.message_id;
      if (!messageId || !state[messageId]) return state;

      return R.assoc(messageId, [...state[messageId]], state);
    }
    default: return state;
  }
};

export default combineReducers({ items, messages });

export const findById = R.curry((state, id) => {
  const comment = state.social.comments.items[id];

  if (!comment) return undefined;

  const user = usersReducer.findById(state, comment.user_id) || comment.user;

  return { ...comment, user };
});

export const findByMessage = R.curry((state, id) => R.map(
  findById(state),
  state.social.comments.messages[id] || [],
));
